import React, { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { STORAGE_KEYS } from "../../../constants/storageKeys";
import { formatAllowance } from "../../../models/vacancy";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { useAppDispatch as useDispatch } from "../../../store";
import { TVacancy, TVacancyDetail } from "../../../store/vacancy/types";
import storage from "../../../utils/storage";
import CancelVacancy from "../../Client/Vacancies/Popup/CancelVacancy";
import ActionDropdown from "../../UI/ActionDropdown";
import CustomButton from "../../UI/Buttons/CustomButton";
import CircleCancel from "../../UI/Icons/CircleCancel";
import DuplicateIcon from "../../UI/Icons/DuplicateIcon";
import EditPencil from "../../UI/Icons/EditPencil";
import Eye from "../../UI/Icons/Eye";
import VerticalDots from "../../UI/Icons/VerticalDots";
import PopupModal from "../../UI/Popup";
import VacancyPosition from "../../UI/VacancyPosition";
import CentreImage from "../../Vacancy/CentreImage";
import { formatTimeframes } from "../../Vacancy/timeframeApiHelper";
import VacancySettings from "../../Vacancy/VacancySettings";

interface VacancyCardProps {
  vacancy: TVacancy;
  type: string;
}

interface TimeFrame {
  date: string;
  start_time: string;
  end_time: string;
}

const VacancyCard: React.FC<VacancyCardProps> = ({ vacancy, type }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  const handlePendingVacancy = (vacancy: any) => {
    storage.set(STORAGE_KEYS.SELECTED_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.ADMIN_PENDING_VACANCIES);
  };

  

  const handleEditVacancy = () => {
    storage.set(STORAGE_KEYS.SELECTED_VACANCY_ID, vacancy?.uuid);
    navigate(AUTH_ROUTE.ADMIN_EDIT_VACANCY);
  };

  const handleDuplicateVacancy = () => {
    storage.set(STORAGE_KEYS.DUPLICATE_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.ADMIN_CREATE_VACANCY);
  };

  const handleCancelVacancy = () => {
    setShowCancelModal(true);
  };

  const onClickClose = () => {
    setShowCancelModal(false);
  };

  const dropDownActionList = [
    ...(vacancy?.applications_count > 0 ? [] : [{ // If no applications, show edit option
      label: "Edit Vacancy",
      icon: <EditPencil />,
      onClick: handleEditVacancy,
    }]),
    {
      label: "Duplicate as New Vacancy",
      icon: <DuplicateIcon />,
      onClick: handleDuplicateVacancy,
    },
    {
      label: "Cancel Vacancy",
      icon: <CircleCancel />,
      onClick: handleCancelVacancy,
    },
  ];

  return (
    <>
      <div className="bg-white p-15px d-flex flex-column flex-lg-row gap-10 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400 text-black">
        <div className="flex-grow-1 w-100 text-break">
          <div className="d-flex gap-20 align-items-center">
            <div className="w-50px h-50px  radius-50px">
              <CentreImage vacancy={vacancy} height="50px" width="50px" />
            </div>
            <div className="flex-grow-1 d-flex gap-10 justify-content-between">
              <div className="d-flex flex-column gap-3 text-black">
                <div className="fs-16 fw-700">
                  <VacancyPosition
                    positions={vacancy?.vacancy_positions}
                    alternatePosition={vacancy?.alternate_position_legacy || ""}
                  />
                </div>
                <div className="fs-13 fw-400">
                  {vacancy?.centre ? vacancy?.centre?.business_name : ""}
                </div>
              </div>
              <div className="min-w-24px position-relative d-block d-lg-none">
                <CustomButton
                  variant={"primary"}
                  ref={toggleButtonRef}
                  type="button"
                  className="btn-theme bg-white p-0 h-auto"
                  onClick={() => {
                    setOpenDropdown((prev) => !prev);
                  }}
                >
                  <VerticalDots color={!openDropdown ? "#aaaaaa" : "#000000"} />
                </CustomButton>
                <ActionDropdown
                  options={dropDownActionList}
                  isOpen={openDropdown}
                  onClose={() => setOpenDropdown(false)}
                  toggleRef={toggleButtonRef}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="min-w-210px w-210px mobile-w-100 text-break">
          <div className="fs-13">
            {
              // type === "urgent" ? (
              //   <div className="text-alert fw-700">{vacancy?.formatted_times}</div>
              // ) : (
              formatTimeframes(vacancy?.timeframes)?.map(
                (formatedTimeData, index) => (
                  <div
                    key={index}
                    className={`fs-13 ${
                      formatedTimeData.alert ? "text-alert fw-700" : "fw-400"
                    }`}
                  >
                    {formatedTimeData.label}
                  </div>
                )
              )
              // )
            }
          </div>
        </div>
        <div className="min-w-100px">
          <div className="fs-13">{vacancy?.posted ? vacancy?.posted : ""}</div>
        </div>
        <div className="min-w-160px w-160px mobile-w-100 text-break d-flex gap-5 flex-wrap">
          <VacancySettings vacancy={vacancy} />
        </div>
        <div className="min-w-100px">
          <div className="fs-13 fw-400">{formatAllowance(Number(vacancy?.allowance) ?? undefined, true)}</div>
        </div>
        <div className="min-w-165px mobile-w-100">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black w-100"
            onClick={() => handlePendingVacancy(vacancy)}
          >
            <Eye color="black" />
            View Details
          </CustomButton>
        </div>
        <div className="min-w-24px position-relative d-none d-lg-block">
          <CustomButton
            variant={"primary"}
            ref={toggleButtonRef}
            type="button"
            className="btn-theme bg-white p-0 h-auto"
            onClick={() => {
              setOpenDropdown((prev) => !prev);
            }}
          >
            <VerticalDots color={!openDropdown ? "#aaaaaa" : "#000000"} />
          </CustomButton>
          <ActionDropdown
            options={dropDownActionList}
            isOpen={openDropdown}
            onClose={() => setOpenDropdown(false)}
            toggleRef={toggleButtonRef}
          />
        </div>
      </div>
      {/* Cancel Modal */}
      <PopupModal
        visible={showCancelModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
      >
        {/* Cancel Modal */}
        {vacancy && (
          <CancelVacancy
            vacancy={vacancy as unknown as TVacancyDetail}
            onClose={onClickClose}
          />
        )}
      </PopupModal>
      {/* Cancel Modal */}
    </>
  );
};

export default VacancyCard;
